import { MIN_PASSWORD_LENGTH, STAFF_ROLE_LABELS_PL, type CurrentUser } from '@cfcg/shared';
import { useState, type FormEvent } from 'react';
import { Alert, Button, Card, Field, Input } from '../components/ui.js';
import { messageOf, useChangeOwnPassword } from '../lib/auth.js';

export function AccountPage({ current }: { current: CurrentUser }) {
  const changePassword = useChangeOwnPassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [mismatch, setMismatch] = useState(false);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== repeat) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setCurrentPassword('');
          setNewPassword('');
          setRepeat('');
        },
      },
    );
  }

  return (
    <div className="space-y-6">
      <Card title="Twoje konto">
        <dl className="grid gap-2 text-sm sm:grid-cols-[10rem_1fr]">
          <dt className="text-neutral-500">Imię i nazwisko</dt>
          <dd className="text-neutral-900">{current.user.displayName}</dd>
          <dt className="text-neutral-500">Adres e-mail</dt>
          <dd className="text-neutral-900">{current.user.email}</dd>
          <dt className="text-neutral-500">Rola</dt>
          <dd className="text-neutral-900">{STAFF_ROLE_LABELS_PL[current.user.role]}</dd>
          <dt className="text-neutral-500">Sesja wygasa</dt>
          <dd className="text-neutral-900">
            {new Date(current.sessionExpiresAt).toLocaleString('pl-PL')}
          </dd>
        </dl>
      </Card>

      <Card title="Zmiana hasła">
        <form onSubmit={onSubmit} className="max-w-sm space-y-4">
          <Field label="Obecne hasło">
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Field>
          <Field
            label="Nowe hasło"
            hint={`Co najmniej ${MIN_PASSWORD_LENGTH} znaków. Liczy się długość, nie znaki specjalne.`}
          >
            <Input
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </Field>
          <Field label="Powtórz nowe hasło">
            <Input
              type="password"
              autoComplete="new-password"
              required
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          </Field>

          {mismatch && <Alert tone="error">Hasła nie są identyczne.</Alert>}
          {changePassword.isError && <Alert tone="error">{messageOf(changePassword.error)}</Alert>}
          {changePassword.isSuccess && (
            <Alert tone="success">Hasło zmienione. Pozostałe urządzenia zostały wylogowane.</Alert>
          )}

          <Button type="submit" disabled={changePassword.isPending}>
            {changePassword.isPending ? 'Zapisywanie…' : 'Zmień hasło'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
